import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import InquireModal from "./InquireModal";
import ApplyConfirmModal from "./ApplyConfirmModal";

// The browser only ever calls our own Netlify function; the EXR API token
// stays server-side.
const LISTINGS_ENDPOINT =
  process.env.REACT_APP_LISTINGS_ENDPOINT || "/.netlify/functions/listings";

// Values used by existing deep links, e.g. /availabilities?type=1-bed
const TYPE_PARAM_TO_BEDS = { studio: 0, "1-bed": 1, "2-bed": 2, "3-bed": 3 };

// Fallback when a listing has no apply_url of its own (same link as the
// contact page's "Looking for a link to apply?").
const GENERIC_APPLY_URL =
  "https://www.exrny.com/apply?utm_source=http%3A%2F%2F150bk.com%2F&utm_medium=referral&utm_campaign=landing_pages";

const bedsLabel = (beds) => (beds === 0 ? "Studio" : `${beds} Bed`);

const sectionTitle = (beds) => {
  if (beds === null) return "More Residences";
  if (beds === 0) return "Studios";
  return `${beds} Bedroom${beds === 1 ? "" : "s"}`;
};

const formatPrice = (price) =>
  price === null
    ? "Inquire for pricing"
    : price.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });

const availabilityLabel = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  if (date.getTime() <= Date.now()) return "Available Now";
  return `Available ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
};

const specsLine = (listing) => {
  const parts = [];
  if (listing.bedrooms !== null) {
    const bed = listing.bedrooms === 0 ? "Studio" : `${listing.bedrooms} Bed`;
    const bath = listing.bathrooms !== null ? ` | ${listing.bathrooms} Bath` : "";
    parts.push(bed + bath);
  } else if (listing.bathrooms !== null) {
    parts.push(`${listing.bathrooms} Bath`);
  }
  if (listing.sqft) parts.push(`${Math.round(listing.sqft).toLocaleString("en-US")} SF`);
  const available = availabilityLabel(listing.availableDate);
  if (available) parts.push(available);
  return parts.join(" · ");
};

export default function ListingCards() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [bedsFilter, setBedsFilter] = useState("all");
  const [buildingFilter, setBuildingFilter] = useState("all");
  const [maxPrice, setMaxPrice] = useState("all");
  const [sort, setSort] = useState("price-asc");

  const [floorplanModal, setFloorplanModal] = useState(null);
  const [inquireListing, setInquireListing] = useState(null);
  const [applyListing, setApplyListing] = useState(null);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const type = searchParams.get("type");
    if (type && type in TYPE_PARAM_TO_BEDS) setBedsFilter(TYPE_PARAM_TO_BEDS[type]);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(LISTINGS_ENDPOINT)
      .then(({ data }) => {
        if (cancelled) return;
        setListings(Array.isArray(data) ? data : data?.listings || []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const bedOptions = useMemo(
    () =>
      [...new Set(listings.map((l) => l.bedrooms).filter((b) => b !== null))].sort(
        (a, b) => a - b
      ),
    [listings]
  );

  const buildingOptions = useMemo(
    () => [...new Set(listings.map((l) => l.building).filter(Boolean))].sort(),
    [listings]
  );

  const priceOptions = useMemo(() => {
    const prices = listings.map((l) => l.price).filter((p) => p !== null);
    if (prices.length < 2) return [];
    const thresholds = [...new Set(prices.map((p) => Math.ceil(p / 500) * 500))].sort(
      (a, b) => a - b
    );
    // The largest threshold covers everything, same as "Any price".
    return thresholds.slice(0, -1).concat(thresholds[thresholds.length - 1]);
  }, [listings]);

  const filtered = useMemo(() => {
    let result = listings.filter(
      (l) =>
        (bedsFilter === "all" || l.bedrooms === bedsFilter) &&
        (buildingFilter === "all" || l.building === buildingFilter) &&
        (maxPrice === "all" || l.price === null || l.price <= maxPrice)
    );
    const byPrice = (a, b) => (a.price ?? Infinity) - (b.price ?? Infinity);
    if (sort === "price-asc") result = [...result].sort(byPrice);
    if (sort === "price-desc") result = [...result].sort((a, b) => byPrice(b, a));
    if (sort === "sqft-desc") result = [...result].sort((a, b) => (b.sqft ?? 0) - (a.sqft ?? 0));
    return result;
  }, [listings, bedsFilter, buildingFilter, maxPrice, sort]);

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((l) => {
      const key = l.bedrooms;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    });
    return [...map.entries()].sort(
      ([a], [b]) => (a ?? Infinity) - (b ?? Infinity)
    );
  }, [filtered]);

  const resetFilters = () => {
    setBedsFilter("all");
    setBuildingFilter("all");
    setMaxPrice("all");
  };

  return (
    <section className="availabilities listings" id="table">
      <h2 className="availabilities-heading" id="availabilitiesHeading">
        Availabilities
      </h2>

      {!loading && !error && listings.length > 0 && (
        <>
          <p className="listings-subtitle">
            {listings.length} residence{listings.length === 1 ? "" : "s"} currently
            available at One Fifty
          </p>

          <div className="listings-toolbar">
            <div className="listings-pills" role="group" aria-label="Filter by residence type">
              <button
                type="button"
                className={bedsFilter === "all" ? "active" : ""}
                aria-pressed={bedsFilter === "all"}
                onClick={() => setBedsFilter("all")}
              >
                All
              </button>
              {bedOptions.map((beds) => (
                <button
                  type="button"
                  key={beds}
                  className={bedsFilter === beds ? "active" : ""}
                  aria-pressed={bedsFilter === beds}
                  onClick={() => setBedsFilter(beds)}
                >
                  {bedsLabel(beds)}
                </button>
              ))}
            </div>
            <div className="listings-selects">
              {buildingOptions.length > 1 && (
                <select
                  aria-label="Building"
                  value={buildingFilter}
                  onChange={(e) => setBuildingFilter(e.target.value)}
                >
                  <option value="all">All Buildings</option>
                  {buildingOptions.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              )}
              {priceOptions.length > 0 && (
                <select
                  aria-label="Maximum price"
                  value={maxPrice}
                  onChange={(e) =>
                    setMaxPrice(e.target.value === "all" ? "all" : Number(e.target.value))
                  }
                >
                  <option value="all">Any Price</option>
                  {priceOptions.map((p) => (
                    <option key={p} value={p}>
                      Up to ${p.toLocaleString("en-US")}
                    </option>
                  ))}
                </select>
              )}
              <select aria-label="Sort listings" value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="sqft-desc">Size: Largest First</option>
              </select>
            </div>
          </div>
        </>
      )}

      <div className="listings-content">
        {loading && (
          <div className="listing-grid" aria-busy="true" aria-label="Loading availabilities">
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="listing-card skeleton" key={i}>
                <div className="listing-card-media" />
                <div className="listing-card-body">
                  <div className="skeleton-line lg" />
                  <div className="skeleton-line" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="listings-state">
            <h3>Availability is Temporarily Unavailable</h3>
            <p>
              We couldn't load current listings. Please refresh the page, or reach out
              and our leasing team will send you the latest availability.
            </p>
            <Link className="listings-cta" to="/contact">
              Contact Us
            </Link>
          </div>
        )}

        {!loading && !error && listings.length === 0 && (
          <div className="listings-state">
            <h3>Nothing Available Right Now</h3>
            <p>
              All residences at One Fifty are currently spoken for. Contact us to hear
              about upcoming availability before it hits the market.
            </p>
            <Link className="listings-cta" to="/contact">
              Contact Us
            </Link>
          </div>
        )}

        {!loading && !error && listings.length > 0 && filtered.length === 0 && (
          <div className="listings-state">
            <h3>No Matches</h3>
            <p>No residences match your current filters.</p>
            <button type="button" className="listings-cta" onClick={resetFilters}>
              Reset Filters
            </button>
          </div>
        )}

        {!loading &&
          !error &&
          groups.map(([beds, group]) => (
            <div className="listing-section" key={beds === null ? "other" : beds}>
              <h3 className="listing-section-heading">
                {sectionTitle(beds)}
                <span>
                  {group.length} available
                </span>
              </h3>
              <div className="listing-grid">
                {group.map((listing) => {
                  const media = listing.photo || listing.floorplan;
                  const isFloorplanMedia = !listing.photo && !!listing.floorplan;
                  return (
                    <article className="listing-card" key={listing.id}>
                      <div className="listing-card-media">
                        {media ? (
                          <img
                            src={media}
                            alt={`Residence ${listing.unit || ""} ${isFloorplanMedia ? "floor plan" : "interior"}`}
                            className={isFloorplanMedia ? "is-floorplan" : ""}
                            loading="lazy"
                          />
                        ) : (
                          <div className="listing-card-placeholder">
                            <span>{listing.unit || sectionTitle(beds)}</span>
                          </div>
                        )}
                        {listing.outdoorSpace && (
                          <span className="listing-badge">{listing.outdoorSpace}</span>
                        )}
                      </div>
                      <div className="listing-card-body">
                        {(listing.building || listing.address) && (
                          <p className="listing-card-building">
                            {listing.building || listing.address}
                          </p>
                        )}
                        <h4 className="listing-card-unit">
                          {listing.unit ? `Residence ${listing.unit}` : "Residence"}
                        </h4>
                        <div className="listing-card-pricing">
                          {listing.netEffectiveRent ? (
                            <>
                              <p className="listing-card-price">
                                {listing.price !== null && (
                                  <s className="gross">{formatPrice(listing.price)}</s>
                                )}
                                <span className="discounted">
                                  {formatPrice(listing.netEffectiveRent)}
                                </span>
                                <span className="permo">/mo</span>
                              </p>
                              {listing.concessionLabel && (
                                <p className="listing-card-deal">{listing.concessionLabel}</p>
                              )}
                            </>
                          ) : (
                            <p className="listing-card-price">
                              {formatPrice(listing.price)}
                              {listing.price !== null && <span className="permo">/mo</span>}
                            </p>
                          )}
                        </div>
                        {specsLine(listing) && (
                          <p className="listing-card-specs">{specsLine(listing)}</p>
                        )}
                      </div>
                      <div className="listing-card-actions">
                        <button
                          type="button"
                          disabled={!listing.floorplan}
                          onClick={() => listing.floorplan && setFloorplanModal(listing.floorplan)}
                        >
                          {listing.floorplan ? "Floor Plan" : "Plan Soon"}
                        </button>
                        <button
                          type="button"
                          className="apply"
                          onClick={() => setApplyListing(listing)}
                        >
                          Apply
                        </button>
                        <button
                          type="button"
                          className="inquire"
                          onClick={() => setInquireListing(listing)}
                        >
                          Inquire
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
      </div>

      {floorplanModal && (
        <div className="show3d-modal availabilities-modal">
          <div className="inner">
            <div className="wrapper">
              <svg
                className="close-show3d-modal"
                onClick={() => setFloorplanModal(null)}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 5L5 19M5 5L19 19"
                  stroke="#ffffff"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
              <img src={floorplanModal} alt="floorplan" />
            </div>
          </div>
        </div>
      )}

      {inquireListing && (
        <InquireModal listing={inquireListing} onClose={() => setInquireListing(null)} />
      )}

      {applyListing && (
        <ApplyConfirmModal
          listing={applyListing}
          applyUrl={applyListing.applyUrl || GENERIC_APPLY_URL}
          onClose={() => setApplyListing(null)}
        />
      )}
    </section>
  );
}
